import type { MapLayerMouseEvent } from 'maplibre-gl';
import { LngLat } from 'maplibre-gl';
import { useMemo, useState } from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Marker } from 'react-map-gl/maplibre';

import { roundCoordinates } from './helpers';
import type { Coordinates } from './types';
import { BambiMapSearch } from 'src/common/BambiMapView/BambiMapSearch';
import type { MapViewProps } from 'src/common/BambiMapView/BambiMapView';
import { BambiMapView } from 'src/common/BambiMapView/BambiMapView';
import colors from 'src/common/theme/colors';

export type OnSelectLocation = (coordinates: Coordinates) => void;

interface GeolocationMapViewProps {
  onSelect: OnSelectLocation;
  coordinates?: LngLat;
  canEditCoordinates: boolean;
}

type InitialViewState = MapViewProps['initialViewState'];

export function GeolocationMapView({
  onSelect,
  coordinates,
  canEditCoordinates,
}: GeolocationMapViewProps) {
  const [marker, setMarker] = useState<LngLat | undefined>(coordinates);
  const onClick = (e: MapLayerMouseEvent) => {
    const roundedCoordinates = roundCoordinates({
      latitude: e.lngLat.lat,
      longitude: e.lngLat.lng,
    });
    setMarker(
      new LngLat(roundedCoordinates.longitude, roundedCoordinates.latitude)
    );
    onSelect(roundedCoordinates);
  };

  const initialViewState: InitialViewState | undefined = useMemo(() => {
    // Customize the initial view state if loading an existing geolocation
    if (coordinates) {
      return {
        zoom: 12,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
      };
    }
    return undefined;
  }, [coordinates]);

  return (
    <div className="h-full">
      <BambiMapView
        styleName="Hybrid"
        mapViewProps={{
          onClick: canEditCoordinates ? onClick : undefined,
          cursor: canEditCoordinates ? 'crosshair' : 'grab',
          initialViewState,
        }}
      >
        <BambiMapSearch flyToZoom={17} markerColor={colors.indigo} />
        {marker && (
          <Marker
            longitude={marker.lng}
            latitude={marker.lat}
            anchor="bottom"
            offset={[0, 5]} // Needed to get the tip of the marker to align with the crosshair
            color={colors.mint}
          />
        )}
      </BambiMapView>
    </div>
  );
}
